import * as api from "../../api"
import type { ChatGroup } from "../../types"
import type { ChatSession } from "../session"
import type { ChatStore } from "../store"
import { call } from "./call"
import { reseed } from "./conversations"

/**
 * Reseed after anything that changes a conversation row, because the row is
 * what the list and header render from (FAME-440) — not the group details.
 * Without it the actor waits for their own broadcast notice to see the change
 * they just made.
 */
function callAndReseed<T>(
  session: ChatSession,
  store: ChatStore,
  fn: (token: string) => Promise<T>
): Promise<T> {
  return call(session, async (token) => {
    const result = await fn(token)
    await reseed(token, store)
    return result
  })
}

/**
 * Every member gets a row server-side at creation, so the group is listable
 * before its first message.
 */
export function createGroup(
  session: ChatSession,
  store: ChatStore,
  name: string,
  memberIds: string[]
): Promise<ChatGroup> {
  return callAndReseed(session, store, (token) =>
    api.createGroup(token, name, memberIds)
  )
}

export function getGroup(session: ChatSession, id: string): Promise<ChatGroup> {
  return call(session, (token) => api.getGroup(token, id))
}

export function renameGroup(
  session: ChatSession,
  store: ChatStore,
  id: string,
  name: string
) {
  return callAndReseed(session, store, (token) =>
    api.renameGroup(token, id, name)
  )
}

export function setGroupArchived(
  session: ChatSession,
  store: ChatStore,
  id: string,
  archived: boolean
) {
  return callAndReseed(session, store, (token) =>
    archived ? api.archiveGroup(token, id) : api.unarchiveGroup(token, id)
  )
}

/** Members ride on the row (FAME-657) and the modal renders from it, so reseed. */
export function addGroupMembers(
  session: ChatSession,
  store: ChatStore,
  id: string,
  memberIds: string[]
) {
  return callAndReseed(session, store, (token) =>
    api.addGroupMembers(token, id, memberIds)
  )
}

export function removeGroupMembers(
  session: ChatSession,
  store: ChatStore,
  id: string,
  memberIds: string[]
) {
  return callAndReseed(session, store, (token) =>
    api.removeGroupMembers(token, id, memberIds)
  )
}

/** Admins ride on the group record, not the row (FAME-574), so no reseed. */
export function promoteGroupAdmin(
  session: ChatSession,
  id: string,
  userId: string
) {
  return call(session, (token) => api.promoteGroupAdmin(token, id, userId))
}

export function demoteGroupAdmin(
  session: ChatSession,
  id: string,
  userId: string
) {
  return call(session, (token) => api.demoteGroupAdmin(token, id, userId))
}

/** User ids -> handles, minting on first sight (FAME-443). */
export function ensureHandles(
  session: ChatSession,
  userIds: string[]
): Promise<Record<string, string>> {
  return call(session, (token) => api.ensureHandles(token, userIds))
}

/** Handles -> user ids, so a peer can be shown as a person. */
export function resolveHandles(
  session: ChatSession,
  handles: string[]
): Promise<Record<string, string>> {
  return call(session, (token) => api.resolveHandles(token, handles))
}
